import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, Upload, Plus, AlertCircle } from "lucide-react";

const PricelistImportPage = () => {
  const [profiles, setProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState("");
  const [newProfileName, setNewProfileName] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // API URL
  const API_URL = "/api";

  useEffect(() => {
    fetchProfiles();
  }, []);

  const fetchProfiles = async () => {
    try {
      const res = await axios.get(`${API_URL}/pricelists/profiles`);
      setProfiles(res.data);
      if (res.data.length > 0 && !selectedProfile) {
        setSelectedProfile(res.data[0].id);
      }
    } catch (error) {
      console.error("Gagal ambil profile", error);
    }
  };

  const createProfile = async () => {
    if (!newProfileName) return;
    try {
      const res = await axios.post(`${API_URL}/pricelists/profiles`, { name: newProfileName });
      setNewProfileName("");
      await fetchProfiles();
      setSelectedProfile(res.data.id);
      toast.success("Pricelist baru berhasil dibuat");
    } catch (error) {
      toast.error("Gagal membuat pricelist baru");
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await axios.get(`${API_URL}/pricelists/template`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Template_Pricelist_Matrix.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Template berhasil didownload");
    } catch (error) {
      toast.error("Gagal download template");
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!selectedProfile) {
      toast.error("Pilih Nama Pricelist dulu!");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    setIsLoading(true);
    const loadToast = toast.loading("Sedang memproses data matriks...");

    try {
      // INI URL YANG BENAR: /api/pricelists/{ID}/upload
      const res = await axios.post(`${API_URL}/pricelists/${selectedProfile}/upload`, formData);
      toast.dismiss(loadToast);
      toast.success(`Sukses! ${res.data.imported_items} harga berhasil diupdate.`);
    } catch (error) {
      toast.dismiss(loadToast);
      console.error(error);
      toast.error(error.response?.data?.detail || "Gagal upload file (Cek Backend Log)");
    } finally {
      setIsLoading(false);
      e.target.value = null;
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Import Pricelist (Matrix)</h1>
        <p className="text-gray-500">Update harga berdasarkan Zona & Berat (Format Baru).</p>
      </div>

      {/* STEP 1: PILIH PROFILE */}
      <div className="bg-white p-6 rounded-xl border shadow-sm space-y-4">
        <h2 className="font-semibold text-lg">1. Pilih Pricelist</h2>
        <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
                <select 
                    className="w-full border p-2.5 rounded-lg bg-gray-50"
                    value={selectedProfile}
                    onChange={(e) => setSelectedProfile(e.target.value)}
                >
                    <option value="" disabled>-- Pilih Nama Pricelist --</option>
                    {profiles.map(p => (
                        <option key={p.id} value={p.id}>{p.name} ({p.service_type})</option>
                    ))}
                </select>
            </div>
            <div className="flex gap-2">
                <input 
                    type="text" 
                    placeholder="Buat Baru (Cth: PUB 2026)" 
                    className="border p-2.5 rounded-lg"
                    value={newProfileName}
                    onChange={(e) => setNewProfileName(e.target.value)}
                />
                <button onClick={createProfile} className="bg-black text-white px-4 rounded-lg">
                    <Plus size={18} />
                </button>
            </div>
        </div>
      </div>

      {/* STEP 2: DOWNLOAD & UPLOAD */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-blue-50 p-6 rounded-xl border border-blue-100">
            <h3 className="font-bold text-blue-900 mb-2">2. Download Template</h3>
            <button onClick={downloadTemplate} className="w-full bg-white text-blue-700 font-bold py-3 rounded-lg border border-blue-200 flex justify-center gap-2">
                <Download size={20} /> Download Excel
            </button>
        </div>

        <div className={`p-6 rounded-xl border-2 border-dashed flex flex-col justify-center text-center ${!selectedProfile ? 'bg-gray-100 opacity-50' : 'bg-white border-green-300'}`}>
            {!selectedProfile ? (
                 <p className="text-gray-400">Pilih Pricelist Dulu</p>
            ) : (
                <div className="relative h-full flex flex-col justify-center items-center">
                    <input 
                        type="file" 
                        accept=".xlsx, .xls"
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        onChange={handleUpload}
                        disabled={isLoading}
                    />
                    {isLoading ? (
                        <p className="font-bold text-green-600 animate-pulse">Sedang Upload...</p>
                    ) : (
                        <>
                            <div className="bg-green-100 p-3 rounded-full mb-3 text-green-600"><Upload size={24} /></div>
                            <h3 className="font-bold text-gray-800">3. Upload Excel Disini</h3>
                        </>
                    )}
                </div>
            )}
        </div>
      </div>
    </div>
  );
};

export default PricelistImportPage;